/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * EntrantRunsPanel — what opens under a row on a multi-day board's "All days".
 *
 * An "All days" row is every run one entrant attached, added up, so there is
 * no single run page behind it. Its › opens this list instead: each of their
 * runs, in day order, each a link to that run's own page — through its split
 * (?run=) for a run attached day by day, or the result's for one attached the
 * single-run way. Read only when opened, from the board's own public query
 * (fetchEntrantRuns), so a closed board costs nothing.
 *
 * Phones get the same list, stacked: the figures drop under the day so a 320px
 * row never pushes the time off the edge.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchEntrantRuns } from './boardApi';
import { eventDayCount, eventDayDate } from './dailyMode';
import { formatClock, formatKm, formatPace } from './boardModel';

const INK = '#241708';
const MUTED = '#6B5844';
const ACCENT = '#C2410C';
const HAIR = 'rgba(36, 23, 8, .10)';
const SOFT = '#FFF8F2';
const mono = { fontVariantNumeric: 'tabular-nums' };

const CSS = `
.erp-list{list-style:none;margin:0;padding:0;display:grid;gap:8px}
.erp-run{display:grid;grid-template-columns:minmax(0,1fr) auto auto auto 18px;align-items:center;gap:6px 18px;
  min-height:48px;padding:8px 14px;border-radius:12px;border:1px solid ${HAIR};background:#fff;
  color:${INK};text-decoration:none}
.erp-run:hover{border-color:rgba(194,65,12,.45)}
.erp-run:focus-visible{outline:2px solid ${ACCENT};outline-offset:2px}
.erp-day{font-weight:700;min-width:0}
.erp-fig{text-align:right;white-space:nowrap;font-size:14px;color:#4A3D33}
.erp-time{font-weight:800;color:${INK}}
.erp-go{color:${ACCENT};font-weight:800;text-align:right}
@media (max-width:760px){
  .erp-run{grid-template-columns:minmax(0,1fr) 18px;padding:10px 12px}
  .erp-day{grid-column:1 / 2}
  .erp-figs{grid-column:1 / 2;grid-row:2;display:flex;gap:12px;flex-wrap:wrap}
  .erp-fig{text-align:left;font-size:13px}
  .erp-time-wide{display:none}
  .erp-go{grid-column:2;grid-row:1 / span 2}
}
@media (min-width:761px){
  .erp-figs{display:contents}
  .erp-time-narrow{display:none}
}
`;

function dayLabel(n, event) {
  if (!Number.isFinite(n) || n < 1) return 'Activity';
  const iso = eventDayDate(n, event?.start_datetime, event?.timezone);
  const date = new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
  });
  return `Day ${n} · ${date}`;
}

export default function EntrantRunsPanel({
  api, eventActivityId, row, raceEvent, id,
}) {
  const lastDay = eventDayCount(raceEvent?.start_datetime, raceEvent?.end_datetime, raceEvent?.timezone);
  const runs = useQuery({
    queryKey: ['board', 'daily', eventActivityId, 'entrant-runs', String(row.id)],
    queryFn: ({ signal }) => fetchEntrantRuns(api, {
      eventActivityId,
      resultId: row.id,
      name: row.athlete_name,
      bib: row.bib,
      lastDay,
      expected: Number(row.activities) || Infinity,
      signal,
    }),
    retry: 1,
  });

  return (
    <div id={id} style={{ padding: '12px 16px 16px', background: SOFT }}>
      <style>{CSS}</style>
      <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: MUTED }}>
        {`${row.athlete_name || 'This athlete'}'s activities`}
      </p>
      {runs.isPending && <p role="status" style={{ margin: 0, fontSize: 14, color: MUTED }}>Reading their activities…</p>}
      {runs.isError && (
        <p role="alert" style={{ margin: 0, fontSize: 14, color: '#8A2C06' }}>
          Could not load their activities.
          {' '}
          <button type="button" onClick={() => runs.refetch()}
            style={{ border: 0, background: 'none', padding: 0, color: ACCENT, fontWeight: 700, cursor: 'pointer', font: 'inherit' }}>
            Try again
          </button>
        </p>
      )}
      {runs.data && runs.data.length === 0 && (
        <p style={{ margin: 0, fontSize: 14, color: MUTED }}>No activities are attached to them yet.</p>
      )}
      {runs.data && runs.data.length > 0 && (
        <ul className="erp-list">
          {runs.data.map((run) => {
            const to = `/results/${eventActivityId}/${run.resultId}${run.splitId > 0 ? `?run=${run.splitId}` : ''}`;
            const day = dayLabel(run.dayNumber, raceEvent);
            return (
              <li key={run.splitId}>
                <Link
                  to={to}
                  className="erp-run"
                  aria-label={`${day}: ${formatKm(run.distanceMeters)} in ${formatClock(run.seconds)}. Open this activity.`}
                >
                  <span className="erp-day">{day}</span>
                  <span className="erp-figs">
                    <span className="erp-fig" style={mono}>{formatKm(run.distanceMeters)}</span>
                    <span className="erp-fig erp-time erp-time-narrow" style={mono}>{formatClock(run.seconds)}</span>
                    <span className="erp-fig" style={mono}>{`${formatPace(run.seconds, run.distanceMeters)} /km`}</span>
                  </span>
                  <span className="erp-fig erp-time erp-time-wide" style={mono}>{formatClock(run.seconds)}</span>
                  <span className="erp-go" aria-hidden="true">›</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
